import React from 'react';
import {
  Image,
  StyleSheet,
  View,
} from 'react-native';
import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import {
  Rating,
  HeaderText,
  BodyText,
  Button,
} from '../../../ui';
import StateLink from '../../../StateLink';
import { ICrossPromotedProvider } from './types';
import formatPriceAndDuration from '../../../../modules/formatters/formatPriceAndDuration';
import { STATIC_ASSET_URL } from '../../../../config';

const styles = StyleSheet.create({
  image100: {
    width: 100,
    height: 100,
  },
  image80: {
    width: 80,
    height: 80,
  },
  image60: {
    width: 60,
    height: 60,
  },
});

const ProviderCard = ({
  provider,
  onClick,
  appearance,
  utmSource,
  showService = true,
  showProfession = false,
  showLocation = false,
}: {
  provider: ICrossPromotedProvider;
  onClick?: (provider: ICrossPromotedProvider) => void;
  appearance: 'large' | 'medium' | 'small';
  utmSource: string;
  showService?: boolean;
  showProfession?: boolean;
  showLocation?: boolean;
}) => {
  const isSmall = appearance === 'small';
  let url: string;
  if (provider.profile_photo?.match(/^http/)) {
    if (provider.profile_photo?.match(/_[0-9]+x[0-9]+.[a-z]{2,6}$/)) {
      url = provider.profile_photo;
    } else {
      url = `${provider.profile_photo}_300x300.jpg`;
    }
  } else {
    url = `${STATIC_ASSET_URL}/${provider.profile_photo}_300x300.jpg`;
  }
  const linkParams = {
    providerId: provider.vanity_url,
    proId: provider.provider_id,
    ncd_term: provider.provider_id,
    ncd_source: utmSource,
  };

  const getImageSize = () => {
    if (isSmall) {
      return styles.image60;
    }
    return appearance === 'large' ? styles.image100 : styles.image80;
  };

  return (
    <Box
      testID={`cross_promotion_modal_provider_${provider.service_id}`}
      paddingBottom={8}
    >
      <StateLink
        to="profile"
        params={linkParams}
        onClick={() => onClick?.(provider)}
        testID="vanity_state_link"
      >
        <Box
          padding={8}
          borderWidth={1}
          borderColor={colors.lightGrey}
          borderStyle="solid"
          color={colors.whiteWash}
        >
          <Inline>
            <Box paddingRight={8}>
              <Box borderColor={colors.lightGrey} borderWidth={1}>
                <Image
                  testID="provider-card-image"
                  style={getImageSize()}
                  source={url}
                />
              </Box>
            </Box>
            <Box flex={1}>
              {!isSmall && (
                <View style={{ alignSelf: 'flex-start' }}>
                  <Rating
                    ratingStars={provider.average_rating}
                    ratingsCount={provider.num_ratings}
                    size={appearance === 'large' ? 14 : 12}
                    smallText={appearance !== 'large'}
                  />
                </View>
              )}
              <HeaderText testID="pro_name" level={isSmall ? 4 : 3}>{provider.name}</HeaderText>
              {!isSmall && showService && (
                <BodyText testID="service_name">{provider.service_name}</BodyText>
              )}
              {!(showService || showProfession) && <Box paddingY={12} />}
              { /* show services and price on  one line if small carousel card */}
              {showProfession && !!provider.profession && (
                <BodyText testID="profession" appearance="small">
                  {provider.profession}
                </BodyText>
              )}
              {showLocation && !!provider.city_state && (
                <BodyText testID="city_state" color={colors.silver} appearance="small">
                  {provider.city_state}
                </BodyText>
              )}
              { /* show services and price on  one line if small carousel card */}
              {showService && (
                isSmall ? (
                  <BodyText testID="price_and_duration" color={colors.silver} appearance="small">
                    {`${provider.service_name} ${formatPriceAndDuration(provider, provider.show_and_up, false)}`}
                  </BodyText>
                ) : (
                  <BodyText testID="price_and_duration" color={colors.silver}>{formatPriceAndDuration(provider, provider.show_and_up)}</BodyText>
                )
              )}
              {(!isSmall && (showService || showProfession)) && (
                <Inline justifyContent="flex-start" paddingTop={8}>
                  <Button testID="learn_more" size="small">Learn More</Button>
                </Inline>
              )}
              { /* move stars from top to bottom for small carousel card */}
              {isSmall && (
                <View style={{ alignSelf: 'flex-start' }}>
                  <Rating
                    ratingStars={provider.average_rating}
                    ratingsCount={provider.num_ratings}
                    size={12}
                    smallText
                  />
                </View>
              )}
            </Box>
            {(isSmall || !(showService || showProfession)) && (
              <Box justifyContent="flex-end" paddingBottom={16} paddingRight={4}>
                <Button testID="learn_more" size="small">
                  {showService ? 'details' : 'Learn More'}
                </Button>
                {!showService && <Box paddingBottom={12} />}
              </Box>
            )}
          </Inline>
        </Box>
      </StateLink>
    </Box>
  );
};

export default ProviderCard;
